import React, { useEffect, useState } from 'react';
import { ethers } from 'ethers';
import { contract, fmt, chainNow } from '../lib';
import Info from './Info';

const pad = (n) => String(n).padStart(2, '0');
export function countdown(sec) {
  if (sec <= 0) return '0s';
  const d = Math.floor(sec / 86400); const h = Math.floor((sec % 86400) / 3600); const m = Math.floor((sec % 3600) / 60); const s = Math.floor(sec % 60);
  if (d > 0) return `${d}d ${pad(h)}h ${pad(m)}m`;
  return h > 0 ? `${h}h ${pad(m)}m ${pad(s)}s` : `${m}m ${pad(s)}s`;
}

// Compact ORE amount: 36.0M, 252M, 8.57M, 1.2K (exact value goes in a title tooltip).
export function short(wei) {
  const n = Number(ethers.formatEther(wei ?? 0n));
  const a = Math.abs(n);
  const f = (v, u) => `${v >= 100 ? v.toFixed(0) : v >= 10 ? v.toFixed(1) : v.toFixed(2)}${u}`;
  if (a >= 1e9) return f(n / 1e9, 'B');
  if (a >= 1e6) return f(n / 1e6, 'M');
  if (a >= 1e3) return f(n / 1e3, 'K');
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

// Short countdown for the season line: "6d 18h", "5h 12m", "4m 09s".
export function cdShort(sec) {
  if (sec <= 0) return 'now';
  const d = Math.floor(sec / 86400); const h = Math.floor((sec % 86400) / 3600); const m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}d ${pad(h)}h`;
  return h > 0 ? `${h}h ${pad(m)}m` : `${m}m ${pad(Math.floor(sec % 60))}s`;
}

// Live emission season: number, countdown to the next season drop, ORE/day now and next, and (optionally) the user's fleet share.
export default function SeasonPanel({ fleet }) {
  const [sch, setSch] = useState(null);
  const [offset, setOffset] = useState(0); // chain time - browser time
  const [tick, setTick] = useState(Date.now() / 1000);

  useEffect(() => {
    const r = contract('EmissionReserve'); const f = contract('Fleet'); const g = contract('Genesis');
    let alive = true;
    const load = async () => {
      // emission starts at pool launch (liveParams().start = 0 until Genesis.openPool); before that, count down to mintEnd
      const [p, cn, tp, mintEnd] = await Promise.all([r.liveParams(), chainNow(), f.totalPower(), g.mintEnd()]);
      const launched = Number(p.start) !== 0;
      const start = launched ? Number(p.start) : Number(mintEnd); const len = Number(p.seasonLength); const max = Number(p.maxSeasons);
      const idx = cn <= start ? 0 : Math.min(Math.floor((cn - start) / len), max);
      const [rate, next, cum, total] = await Promise.all([r.rateAt(idx), r.rateAt(idx + 1), r.cumulative(cn), r.totalScheduled()]);
      if (!alive) return;
      setOffset(cn - Date.now() / 1000);
      setSch({ start, len, max, idx, rate, next, cum, total, tp, cumAt: cn, launched });
    };
    load().catch(() => {});
    const t = setInterval(() => setTick(Date.now() / 1000), 1000);
    const re = setInterval(() => load().catch(() => {}), 60000);
    return () => { alive = false; clearInterval(t); clearInterval(re); };
  }, []);

  if (!sch) return <div className="card sp" data-testid="season" style={{ marginTop: '1rem' }}><p className="sp-hint">Loading mining info…</p></div>;
  const now = tick + offset;
  const before = !sch.launched || now < sch.start;
  const idx = before ? 0 : Math.floor((now - sch.start) / sch.len);
  const ended = idx >= sch.max;
  const nextDrop = sch.start + (idx + 1) * sch.len;
  const stale = !before && !ended && idx !== sch.idx; // a season boundary passed since the last read
  const rate = stale ? sch.next : sch.rate;
  const nextRate = stale ? (sch.next * 7n) / 10n : sch.next; // refreshed from chain within 60 s
  const emitted = sch.cum + (before || ended ? 0n : (rate * BigInt(Math.max(0, Math.floor(now - sch.cumAt)))) / 86400n);
  const seasonTotal = (rate * BigInt(sch.len)) / 86400n;
  const last = idx + 1 >= sch.max;

  const power = fleet ? fleet.power : 0n;
  const ready = fleet ? fleet.ready : 0n;
  const share = power > 0n && sch.tp > 0n ? Number((power * 1000000n) / sch.tp) / 1e4 : 0;
  const myDay = power > 0n && sch.tp > 0n && !before && !ended ? (rate * power) / sch.tp : 0n;
  const earning = power > 0n || ready > 0n;
  const pctTxt = share >= 99.995 ? '100' : share >= 10 ? share.toFixed(1) : share.toFixed(2);

  const lenTxt = sch.len >= 86400 ? `${sch.len / 86400} days` : `${Math.round(sch.len / 60)} min`;
  const seasonRows = before ? [['Season length', lenTxt], ['First season', `${short((sch.rate * BigInt(sch.len)) / 86400n)} ORE`], ['Each next season', '70% of the last']]
    : [['This season', `${short(seasonTotal)} ORE`], [last ? 'Ends in' : 'Next season in', cdShort(nextDrop - now)], ['Each next season', '70% of the last'],
      ['Mined so far', `${short(emitted)} ORE`], ['All seasons', `${short(sch.total)} ORE`]];
  const strip = (
    <div className="sp-strip" data-testid="season-line">
      {fleet && fleet.mined !== null && fleet.mined !== undefined && fleet.mined > 0n && (
        <span className="sp-si" data-testid="fleet-mined"><span title="All ORE the game has paid you (mining + pirate loot). Not your wallet balance; ORE spent on builds, upgrades or repairs is not subtracted.">Earned (lifetime)</span><b title={`${fmt(fleet.mined, 0)} ORE`}>{short(fleet.mined)} ORE</b></span>)}
      <span className="sp-si"><span>Season</span><b className="sp-c">{ended ? 'Ended' : `${before ? 1 : idx + 1} / ${sch.max}`}</b><Info label="Seasons" rows={seasonRows} testid="season-info" /></span>
      <span className="sp-si sp-si-wide"><span>All miners</span>
        {ended ? <b>0/day</b> : <><b className="sp-c" title={`${fmt(rate, 0)} ORE per day`}>{short(before ? sch.rate : rate)}/day</b>
          <small>{before ? 'once the pool opens' : last ? `last season · ends in ${cdShort(nextDrop - now)}` : `next ${short(nextRate)} in ${cdShort(nextDrop - now)}`}</small></>}
      </span>
    </div>
  );

  return (
    <div className="card sp" data-testid="season" style={{ marginTop: '1rem' }}>
      {earning && !before && (
        <div className="sp-hero" data-testid="season-fleet">
          <div className="sp-hcell">
            <p className="sp-label">You earn</p>
            <p className="sp-big" data-testid="season-mine" title={`≈${fmt(myDay, 0)} ORE per day, before fuel and tax`}>≈{short(myDay)}<span className="sp-unit"> ORE/day</span></p>
            <p className="sp-sub">{String(power)} power · {pctTxt}% of all miners</p>
          </div>
          <div className="sp-hcell">
            <p className="sp-label">Ready to collect</p>
            <p className="sp-big" data-testid="fleet-claimable" title={`${fmt(ready, 0)} ORE`}>{short(ready)}<span className="sp-unit"> ORE</span></p>
            <p className="sp-sub">{fleet.net === null || fleet.net === undefined ? '…' : <>≈ {short(fleet.net)} after Safe tax & fuel{fleet.netRows && <Info label="Safe route" rows={fleet.netRows} testid="net-info" />}</>}</p>
            {fleet.actions && <div className="sp-act">{fleet.actions}</div>}
          </div>
        </div>
      )}
      {(!earning || before) && <p className="sp-hint" data-testid="season-hint">{before ? 'Mining starts when the pool opens.' : 'Send a miner to mine to start earning.'}{fleet && fleet.actions && <span className="sp-act sp-act-inline">{fleet.actions}</span>}</p>}
      {fleet && fleet.note && <p className="sp-paid">{fleet.note}</p>}
      {strip}
    </div>
  );
}
